import { Injectable } from '@angular/core';

import { CapitanLocation, LocationArea, LocationAreaFilters, MXMerchantUnregisteredTerminal, StripeReader } from '../../models/location';
import { ListResponse } from '../../models/list-response';

import { ApiService } from '../api/api.service';
import { environment } from '../../../environments/environment';
import { HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  /**
   * A service providing access to locations via the API.
   */

  // A local copy is kept in memory so that components do not need to load the
  // locations each time they use them.
  private locations: CapitanLocation[] = null;

  // The server error code experienced when trying to load the list of
  // locations, if any. This is not directly available to components; it must
  // be obtained via getLocationsServerError.
  private locationsServerError: number = null;

  // The load ensureLocationsLoaded started, while it is in flight, so that
  // concurrent callers share one request instead of each firing their own — a
  // dashboard mounts up to twelve tiles at once, and every one of them needs
  // the locations before it can render.
  private inFlightLoad: Promise<void> = null;

  constructor(
    private apiService: ApiService
  ) {}

  /**
   * Load the list of locations from the API.
   *
   * @return A promise which resolves when the locations are loaded
   *   (and therefore is available via getLocations).
   */
  loadLocations(): Promise<void> {
    this.locationsServerError = null;

    return this.apiService
      .getCollectionNoFilters<CapitanLocation>('gyms/', 1, 100)
      .then(response => {
        this.locations = response.results;
      })
      .catch(reason => {
        this.locationsServerError = reason.status;
      });
  }

  /**
   * Resolve once the locations are available, loading them only if they aren't
   * already — for callers that must not read `getLocations()` before it can
   * answer.
   *
   * `getLocations()` returns null until the first load resolves, and callers
   * that treat null as "no locations" silently get single-location behaviour in
   * a multi-location org. Awaiting this first removes that race without making
   * every caller own a load.
   *
   * A failed load leaves the locations unloaded (loadLocations swallows the
   * error into `locationsServerError`), so the next caller retries rather than
   * being stuck behind a settled, failed promise.
   */
  ensureLocationsLoaded(): Promise<void> {
    if (this.locations !== null) {
      return Promise.resolve();
    }
    if (this.inFlightLoad === null) {
      this.inFlightLoad = this.loadLocations().then(() => {
        this.inFlightLoad = null;
      });
    }
    return this.inFlightLoad;
  }

  getNumLocations(): number {
    if (this.locations === null) { return null; }

    return this.locations.length;
  }

  /**
   * Return a deep copy of the list of locations. A deep copy is used to prevent
   * components from making non-API-synced edits which would affect other components.
   *
   * @return The list of locations.
   */
  getLocations(): CapitanLocation[] | null {
    if (this.locations === null) { return this.locations; }

    const locationsCopy = [];

    // Note: this only works a long as Location has no nested objects.
    for (const location of this.locations) {
      const locationCopy = {};
      for (const key of Object.keys(location)) {
        locationCopy[key] = location[key];
      }
      locationsCopy.push(locationCopy);
    }

    return locationsCopy as CapitanLocation[];
  }

  getLocationsServerError(): number {
    return this.locationsServerError;
  }

  /**
   * Get an individual location from the API.
   *
   * @param id - The database ID of the location.
   * @return The location.
   */
  getLocation(id: number): Promise<CapitanLocation> {
    return this.apiService.getItem<CapitanLocation>(
      'gyms/',
      id
    );
  }

  /**
   * Update a location's general settings by making a PATCH request to the API.
   *
   * @param location - The location, with updated values.
   * @return The updated location returned from the API.
   */
  updateLocationGeneralSettings(location: CapitanLocation): Promise<CapitanLocation> {
    return this.apiService
      .patchToAPI<CapitanLocation>(
        location.url,
        {
          capacity: location.capacity,
          open_date: location.open_date,
          combine_same_day_check_ins: location.combine_same_day_check_ins,
          check_in_required_proficiency_ids: location.check_in_required_proficiency_ids,
          check_in_required_proficiencies_match_mode: location.check_in_required_proficiencies_match_mode,
          vat_info: location.vat_info,
          allow_climber_app_home_location_selection: location.allow_climber_app_home_location_selection,
          allow_climber_app_product_selection: location.allow_climber_app_product_selection,
          mailchimp_check_in_event_name: location.mailchimp_check_in_event_name,
          xero_tracking_category_name: location.xero_tracking_category_name,
          xero_tracking_category_option_name: location.xero_tracking_category_option_name,
        }
      ).then(updatedLocation => {
        return this.loadLocations()
          .then(() => Promise.resolve(updatedLocation));
      });
  }

  /**
   * Update a location's event settings by making a PATCH request to the API.
   *
   * @param location - The location, with updated values.
   * @return The updated location returned from the API.
   */
  updateLocationEventSettings(location: CapitanLocation): Promise<CapitanLocation> {
    return this.apiService.patchToAPI<CapitanLocation>(
      location.url,
      {
        scheduler_url_path: location.scheduler_url_path,
        scheduler_landing_page_text: location.scheduler_landing_page_text,
        scheduler_first_day_of_week: location.scheduler_first_day_of_week,
        default_staff_site_events_view: location.default_staff_site_events_view,
      }
    );
  }

  /**
   * Get a page of location areas from the API.
   *
   * @param pageNumber - The requested page number.
   * @param pageSize - The requested number of areas per page.
   * @param filters - The set of filters to apply to the areas.
   * @return The list of areas.
   */
  getLocationAreas(pageNumber?: number,
                   pageSize?: number,
                   filters?: LocationAreaFilters): Promise<ListResponse<LocationArea>> {
    return this.apiService.getCollection<LocationArea, LocationAreaFilters>(
      'gym-areas/',
      pageNumber,
      pageSize,
      filters
    );
  }

  /**
   * Post a new location area to the API.
   *
   * @param locationArea - The new LocationArea object to create.
   * @return The new location area returned from the API.
   */
  addLocationArea(locationArea: LocationArea): Promise<LocationArea> {
    return this.apiService.postToAPI<LocationArea>(
      'gym-areas/',
      {
        name: locationArea.name,
        capacity: locationArea.capacity,
        allow_only_one_booked_event: locationArea.allow_only_one_booked_event,
        gym_id: locationArea.gym_id
      }
    );
  }

  /**
   * Update a location area by making a PUT request to the API.
   *
   * @param locationArea - The location area, with updated values.
   * @return The updated location area returned from the API.
   */
  updateLocationArea(locationArea: LocationArea): Promise<LocationArea> {
    return this.apiService.putToAPI<LocationArea>(
      locationArea.url,
      locationArea
    );
  }

  /**
   * Delete a location area by making a DELETE request to the API.
   *
   * @param locationAreaUrl - The API URL of the location area to delete.
   * @return A promise which resolves when the location area is deleted.
   */
  deleteLocationArea(locationAreaUrl: string): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      locationAreaUrl
    ).then();
  }

  getTerminals(locationId?: number): Promise<StripeReader[]> {
    let params = new HttpParams();
    if (locationId) {
      params = params.set('location_id', locationId.toString());
    }

    return this.apiService.getFromAPI<StripeReader[]>(
      'gym-endpoints/stripe-readers/list/',
      params
    );
  }

  addStripeReader(registrationCode: string,
                  label: string,
                  locationId: number): Promise<StripeReader> {
    return this.apiService.postToAPI<StripeReader>(
      'gym-endpoints/stripe-readers/add/',
      {
        registration_code: registrationCode,
        label,
        location_id: locationId
      }
    );
  }

  getUnregisteredMXMerchantTerminals(connId: number): Promise<MXMerchantUnregisteredTerminal[]> {
    return this.apiService.getFromAPI<MXMerchantUnregisteredTerminal[]>(
      'gym-endpoints/stripe-readers/get-mx-merchant-options/' + connId.toString() + '/'
    );
  }

  addMXMerchantTerminal(mxMerchantTerminalId: string,
                        label: string,
                        locationId: number): Promise<StripeReader> {
    return this.apiService.postToAPI<StripeReader>(
      'gym-endpoints/stripe-readers/add-mx-merchant/',
      {
        mx_merchant_id: mxMerchantTerminalId,
        label,
        location_id: locationId
      }
    );
  }

  updateTerminal(reader: StripeReader): Promise<StripeReader> {
    return this.apiService.patchToAPI<StripeReader>(
      environment.api_url + 'gym-endpoints/stripe-readers/update/' + reader.id.toString() + '/',
      {
        label: reader.label
      }
    );
  }

  removeTerminal(reader: StripeReader): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      environment.api_url + 'gym-endpoints/stripe-readers/remove/' + reader.id.toString() + '/'
    ).then();
  }

}
